<template>
  <SplitViewTemplate
    :screenConfig="data"
    :backButtonRoute="'/settings/intake'"
    :dataModel="formModel"
    :formConfig="setFormConfig"
    :setLoading="setLoading"
    :validationSummary="validationSummary"
    :ref="'bin-comments'"
    :defaultUserSettings="false"
  />
</template>
<script>
import { FeatureStorage } from "@/_services/storage";
import SplitViewTemplate from "../../../templates/split-view";
import { SplitViewService } from "../../../_services/api/templates/split-view.services";
import RouteMixin from "@/_mixins/route.mixin";

// Site is a fixed two-value dropdown - see technical-design.md §6.
const siteOptions = [
  { code: "A", name: "Almondco" },
  { code: "S", name: "Simarloo" },
];

export default {
  mixins: [RouteMixin],
  components: {
    SplitViewTemplate,
  },
  data() {
    return {
      setLoading: false,
      validationSummary: [],
      formModel: {
        Code: null,
        Site: null,
        Comment: null,
        Inactive: false,
        ChangedBy: "",
        CreatedBy: "",
        DateChanged: "",
        DateCreated: "",
      },
      data: {
        title: "Bin Comments",
        pageRoute: "/bin-comments",
        saveEndPoint: "AlmondcoBinComment",
        // NOTE (#76198): (Code, Site) is the true composite key (BR-01/BR-02),
        // but the API's PATCH is keyed on RecordId instead - the standard
        // single-key OData shape, matching the List/Review read+navigation
        // approach above and every sibling screen (AlmondcoBinCommentController
        // resolves RecordId -> (Code, Site) internally; BR-01/02/09 are
        // unaffected, they run in AlmondcoBinCommentBusiness against Code/Site
        // directly regardless of which column the route itself is keyed on).
        updateEndPoint: "AlmondcoBinComment",
        updateKeyField: "RecordId",
        updateKeyFieldType: "Guid",
        recordEndPoint: "AlmondcoBinComment",
        exportFileName: "Bin Comments List",
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "AlmondcoBinComment",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
              sort: [{ selector: "Comment", desc: false }],
            },
            rowSelect: {
              view: this,
              page: "bin-comments",
              mode: "review",
              // RecordId (Guid), not Code/Site - Code is free text and can
              // contain characters (e.g. "/") that break a URL path built
              // from it. RecordId is already a real column on this table
              // and needs no special-case routing/filter handling - this
              // screen uses the exact same generic single-key mechanism
              // every other split-view screen uses.
              idProperty: "RecordId",
              idMode: "Guid",
              rowSelectCallBack: () => {
                this.beforeInit();
              },
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/bin-comments/create",
                clickAction: () => {
                  this.goToCreateBinComment();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoBinComments",
                  "create",
                ),
              },
            ],
          },
          columns: [
            {
              screenField: "Code",
              dataField: "Comment",
              label: "Bin Comment",
              allowHiding: false,
              sortIndex: 0,
              sortOrder: "asc",
              cellTemplate: "Record",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "Comment",
                  },
                ],
                secondary: [
                  {
                    dataField: "Code",
                  },
                ],
              },
            },
            {
              screenField: "Code",
              dataField: "Code",
              label: "Code",
              visible: false,
              showInColumnChooser: true,
            },
            {
              screenField: "Comment",
              dataField: "Comment",
              label: "Comment",
              visible: false,
              showInColumnChooser: true,
            },
            {
              // Site's grid cell (via native DevExtreme `lookup`) and its
              // filter-panel tag box (via dataSourceConfig/valueExpr/
              // displayExpr) are two independent mechanisms - see
              // product-location.vue and call-log-list.vue precedent.
              // "ColumnValue" is a generic registry placeholder reused only
              // to satisfy gc-form.vue's screenField lookup requirement for
              // filter fields - it carries no meaning of its own here.
              screenField: "ColumnValue",
              gridDataField: "Site",
              filterDataField: "Site",
              label: "Site",
              filterLabel: "Site",
              filterIndex: 0,
              type: "String",
              // Bug #76849: some legacy rows hold a lowercase Site ("s")
              // rather than the fixed uppercase codes below - DevExtreme's
              // lookup does a strict-equality match against valueExpr, so a
              // lowercase raw value silently rendered a blank cell instead
              // of "Simarloo"/"Almondco". calculateCellValue normalizes the
              // raw value's case before it reaches lookup's own dataSource
              // match - lookup itself is untouched, still resolving the
              // friendly label exactly as before for already-correct rows.
              calculateCellValue: (rowData) =>
                rowData.Site ? rowData.Site.toUpperCase() : rowData.Site,
              lookup: {
                dataSource: [
                  { Code: "A", Name: "Almondco" },
                  { Code: "S", Name: "Simarloo" },
                ],
                valueExpr: "Code",
                displayExpr: "Name",
              },
              dataSourceConfig: {
                data: [
                  { key: "A", value: "Almondco" },
                  { key: "S", value: "Simarloo" },
                ],
              },
              valueExpr: "key",
              displayExpr: "value",
              filterDefaultValue: ["A", "S"],
              width: 150,
            },
            {
              screenField: "Status",
              width: "auto",
              filterIndex: 1,
            },
          ],
          valueChangedEventListners: [],
        },
      },
      updatedDefaultRecordId: undefined,
      recordEditable: true,
    };
  },
  computed: {
    setFormConfig() {
      const form = {
        headerToolbar: {
          closeSplit: true,
          save: this.setSaveOption,
          audit: true,
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
              ],
            },
          ],
          toolbarFormItems: [
            {
              screenField: "Inactive",
              editorType: "dxSwitch",
              editorOptions: {
                readOnly: false,
              },
              cssClass: "toolbar-customer-label",
            },
          ],
          toolbarFormLocation: "after",
          editCallBack: () => {
            this.beforeInit();
          },
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "Code",
                editorType: "dxTextBox",
                editorOptions: {
                  min: 0,
                  tabIndex: 1,
                  // Carried forward from legacy's own UI limit as the safer
                  // default - FLAG-CONCEPT-04, pending PO confirmation.
                  maxLength: 5,
                  // onValueChanged: this.checkForDuplicateBinComment,
                },
                required: true,
                isPrimaryKeyField: true,
                label: { text: "Code" },
                visible: true,
                allowUpdate: this.baseView.mode == "create" ? true : false,
              },
              {
                dataField: "Site",
                editorType: "dxSelectBox",
                required: true,
                isPrimaryKeyField: true,
                label: { text: "Site" },
                editorOptions: {
                  tabIndex: 2,
                  dataSource: siteOptions,
                  valueExpr: "code",
                  displayExpr: "name",
                  searchEnabled: true,
                  itemTemplate: (item) => {
                    return `<div class="custom-auto-complete" id="${item.code}">
                            <span class="auto-complete-primary">
                              ${item.name}
                            </span>
                          </div>`;
                  },
                  // onValueChanged: this.checkForDuplicateBinComment,
                },
                visible: true,
                allowUpdate: this.baseView.mode == "create" ? true : false,
              },
              {
                dataField: "Comment",
                editorType: "dxTextBox",
                editorOptions: {
                  min: 0,
                  tabIndex: 3,
                  maxLength: 50,
                  onFocusIn: (e) => {
                    e.event.target.select();
                  },
                },
                required: true,
                label: { text: "Comment" },
                visible: true,
                allowUpdate: this.recordEditable,
              },
            ],
          },
        ],
        overWritePayload: this.overWritePayload,
        formContentReady: this.onFormContentReady,
        formHeight: 120,
      };

      return form;
    },
    setSaveOption() {
      // Matches grower-regions.vue exactly - 'edit', not 'update' (the term
      // technical-design.md §6 uses; the real shipped sibling code says
      // 'edit', already established as the pattern to follow, #76191).
      return FeatureStorage.checkPermission(
        "AlmondcoBinComments",
        this.baseView.mode == "create" ? "create" : "edit",
      );
    },
  },
  methods: {
    onFormContentReady(e) {
      // Bug #76849: normalize a legacy lowercase Site value ("s") to the
      // uppercase code siteOptions/valueExpr:"code" actually holds - the
      // dxSelectBox's own value-match is strict-equality too, so without
      // this the Review/Edit panel's Site field rendered as an unselected
      // "Select..." placeholder instead of "Simarloo"/"Almondco", even
      // though the underlying value was a technically-valid code. Runs for
      // every mode (Review/Edit/Create all bind through this same
      // formModel.Site) - a no-op in create mode, where Site starts null.
      // Confirmed safe to mutate formModel directly here: split-view.vue
      // has already fully populated it before this callback fires, nothing
      // in split-view.vue/gc-form.vue tracks a "dirty" state off this
      // object, and BR-02 (overWritePayload) strips Site from the payload
      // on every edit save regardless, so this can't change what's sent
      // back to the API.
      if (this.formModel.Site) {
        this.formModel.Site = this.formModel.Site.toUpperCase();
      }
      if (this.baseView.mode == "create") {
        this.$nextTick(() => {
          if (e) {
            e.component.getEditor("Code").focus();
            this.headerFormInstance = e.component;
          }
        });
      } else if (this.baseView.mode == "edit") {
        this.$nextTick(() => {
          if (e) {
            e.component.getEditor("Comment").focus();
            this.headerFormInstance = e.component;
          }
        });
      }
    },
    goToCreateBinComment() {
      this.$router
        .push({
          path: `/bin-comments/create`,
        })
        .then(() => {
          this.recordEditable = true;
        });
    },
    beforeInit() {
      this.recordEditable = true;
    },
    overWritePayload(payload) {
      const activeElement = document.activeElement;
      if (activeElement) {
        activeElement.blur();
      }
      // BR-02: Code/Site are immutable after create - the WebApp never
      // includes them in an edit's payload (the API tolerates an unchanged
      // same-value echo either way, but this matches the design's explicit
      // statement).
      if (this.baseView.mode == "edit") {
        delete payload.Code;
        delete payload.Site;
      }
      return payload;
    },
    // BR-09 UX (Story 2 AC): on Code/Site blur in create mode, check for an
    // already-existing (Code, Site) pair and load it into the panel in edit
    // mode instead of letting the operator continue building a duplicate.
    // No shipped precedent for this exact mechanic exists anywhere in the
    // codebase (confirmed by reading console-season-year.vue directly -
    // technical-design.md §9 attributes it there, but it isn't actually
    // implemented) - built here via a light, direct API query rather than
    // reading the grid's in-memory rows, per developer confirmation. The
    // server-side BR-09 rejection on POST (#76197) remains the backstop for
    // a genuine create-race either way.
    async checkForDuplicateBinComment() {
      if (this.baseView.mode !== "create") return;

      const code = this.formModel.Code;
      const site = this.formModel.Site;
      if (!code || !site) return;

      await SplitViewService.getItems({
        url: "api/AlmondcoBinComment",
        filter: `Code eq '${code}' and Site eq '${site}'`,
        top: "1",
      }).then((res) => {
        if (res && res.data && res.data.value && res.data.value.length > 0) {
          const match = res.data.value[0];
          this.$notify(
            `A Bin Comment for Code "${code}" and Site "${site}" already exists - opening it for editing.`,
            "Warning",
          );
          this.$router.push({
            path: "/bin-comments/edit/" + match.RecordId,
          });
        }
      });
    },
  },
  created() {
    this.beforeInit();
  },
};
</script>
